import "core-js/modules/web.dom-collections.iterator";
import "core-js/modules/web.dom-collections.iterator";
/**
 * Component: Product gallery
 * ------------------------------------------------------------------------------
 * An Product gallery file that contains scripts to function.
 * Zoom functionality is using Drift.
 * - https://github.com/imgix/drift#basic-usage
 *
 * @namespace productGallery
 */

import { debounce, on } from '~/helpers/utils';
import ProductVideo from './product-video';
import Carousel from '~/components/carousel';
/**
 * DOM selectors.
 */

const selectors = {
  container: '[js-product-gallery="container"]',
  featuredImage: '[js-product-gallery="featuredImage"]',
  featuredCarousel: '[js-product-gallery="featuredCarousel"]',
  thumbnail: '[js-product-gallery="thumbnail"]',
  thumbnailCarousel: '[js-product-gallery="thumbnailCarousel"]',
  zoomPane: '[js-product-gallery="zoomPane"]',
  sizeGuide: '[js-product-form="size-guide"]',
  galleryVideo: '[js-product-video="video"]',
  swatches: '[js-product-swatches="option"]'
};
/**
 * Export a new product gallery.
 */

export default (() => {
  /**
   * DOM node selectors.
   */
  const nodeSelectors = {
    container: [...document.querySelectorAll(selectors.container)],
    featuredImage: [...document.querySelectorAll(selectors.featuredImage)],
    featuredCarousel: document.querySelector(selectors.featuredCarousel),
    thumbnail: [...document.querySelectorAll(selectors.thumbnail)],
    thumbnailCarousel: document.querySelector(selectors.thumbnailCarousel),
    zoomPane: document.querySelector(selectors.zoomPane),
    sizeGuide: document.querySelector(selectors.sizeGuide),
    galleryVideo: document.querySelectorAll(selectors.galleryVideo),
    swatches: [...document.querySelectorAll(selectors.swatches)]
  };
  /**
   * Instance globals.
   */

  const namespace = nodeSelectors.featuredCarousel;
  let featuredCarousel = {};
  let featuredCarouselLength = 0;
  let thumbnailCarousel = {};
  let newSlides = [];
  let newThumbnails = [];
  /**
   * Initialise component.
   */

  function init() {
    setDefaultState();
    setEventBusListeners();
    setEventListeners();
  }
  /**
   * Set event listeners.
   */


  function setEventListeners() {
    on('resize', debounce(handleResizeEvent, 250));

    if (nodeSelectors.sizeGuide) {
      on('click', nodeSelectors.sizeGuide, event => {
        event.preventDefault();
        featuredCarousel.select(featuredCarouselLength);
      });
    }
  }
  /**
   * Listen for product form change events.
   */


  function setEventBusListeners() {
    Frame.EventBus.listen('ProductSwatches:optionSelected', response => updateSlides(response));
    Frame.EventBus.listen("Carousel-".concat(namespace.id, ":transitionStart"), response => {
      Frame.EventBus.emit('VideoPlayer:pause');
      handleCarouselChange(response.selectedIndex);
    });
  }
  /**
   * Construct carousel objects on gallery images.
   */


  function constructCarousels() {
    thumbnailCarousel = Carousel(selectors.thumbnailCarousel, {
      swiper: {
        cellSelector: selectors.thumbnail,
        freeMode: false,
        keyboard: false,
        loop: false,
        slidesPerView: 4,
        spaceBetween: 0,
        watchSlidesProgress: true,
        watchSlidesVisibility: true,
        direction: 'vertical',
        touchRatio: 0,
        threshold: 1000
      }
    });
    thumbnailCarousel.init();
    const thumbs = thumbnailCarousel.getSwiperInstance();
    featuredCarousel = Carousel(selectors.featuredCarousel, {
      swiper: {
        autoHeight: false,
        cellSelector: selectors.featuredImage,
        loop: false,
        loopedSlides: 4,
        spaceBetween: 10,
        navigation: {
          nextEl: '.swiper-button-next',
          prevEl: '.swiper-button-prev'
        },
        thumbs: {
          swiper: thumbs
        }
      }
    });
    featuredCarousel.init();
  }
  /**
   * Set default state based on screen size.
   */


  function setDefaultState() {
    constructCarousels();
    initCarousels();
  }
  /**
   * Initialise gallery and thumbnail carousels
   */


  function initCarousels() {
    let firstAvailableSwatch = [...nodeSelectors.swatches].find(swatch => {
      return swatch.dataset.available === 'true';
    });

    if (firstAvailableSwatch === undefined) {
      firstAvailableSwatch = [...nodeSelectors.swatches][0];
    }

    const defaultSwatch = {
      target: firstAvailableSwatch
    };
    nodeSelectors.thumbnail.forEach(element => {
      const min = 1;
      const max = 5;
      const shapeClass = "shape-".concat(Math.floor(Math.random() * (max - min + 1)) + min);
      element.classList.add(shapeClass);
    });
    updateSlides(defaultSwatch);
  }
  /**
   * Handle resize events through debounce callback.
   * - Disable zoom on small devices.
   * - Remove carousel on thumbnails on large screens.
   */


  function handleResizeEvent() {
    featuredCarousel.update();
    thumbnailCarousel.update();
  }

  function handleCarouselChange(index) {
    if (index > 0) {
      Frame.EventBus.emit('Badge:disable');
    } else {
      Frame.EventBus.emit('Badge:enable');
    }
  }
  /**
   * Update to carousel slides.
   * @param {String} variant - name of variant selected.
   */


  function updateSlides(response) {
    const {
      target
    } = response;
    const selectedVariant = target.dataset.value;
    const newImages = generateHtmlSlides(selectedVariant);
    const newThumbs = generateHtmlThumbs(selectedVariant);
    emptyCarouselSlides();
    insertCarouselSlides(newImages, newThumbs);
    ProductVideo().init();
  }
  /**
   * Reset carousel back to slide index 0.
   */


  function resetCarousels() {
    thumbnailCarousel.update();
    featuredCarousel.update();
    featuredCarouselLength = document.querySelectorAll(selectors.thumbnail).length - 1;
    featuredCarousel.select(0, 0);
  }
  /**
   * Generate images to be inserted.
   */


  function generateHtmlSlides(variant) {
    newSlides = [];
    const slides = nodeSelectors.featuredImage.filter(slide => {
      return slide.attributes['js-product-variant'].value === variant.toLowerCase() || slide.attributes['js-product-variant'].value === 'general';
    });
    newSlides.push(slides);
    return slides;
  }
  /**
   * Generate thumbnails to be inserted.
   */


  function generateHtmlThumbs(variant) {
    newThumbnails = [];
    const thumbs = nodeSelectors.thumbnail.filter(thumbnail => {
      return thumbnail.attributes['js-product-variant'].value === variant.toLowerCase() || thumbnail.attributes['js-product-variant'].value === 'general';
    });
    newThumbnails.push(thumbs);
    return thumbs;
  }
  /**
   * Insert new slides into carousel.
   */


  function insertCarouselSlides(slides, thumbs) {
    featuredCarousel.addSlides(1, slides);
    thumbnailCarousel.addSlides(1, thumbs);
    resetCarousels();
  }
  /**
   * Check if variant image is available.
   * @param {Number} id - Variant id of selected option.
   * @returns {Boolean}
   */


  function isVariantImageAvailable(id) {
    return Boolean(document.querySelector("".concat(selectors.thumbnail, "[data-variant-id=\"").concat(id, "\"")));
  }
  /**
   * Empty carousels.
   */


  function emptyCarouselSlides() {
    featuredCarousel.removeAllSlides();
    thumbnailCarousel.removeAllSlides();
  }
  /**
   * Expose public interface.
   */


  return Object.freeze({
    init,
    isVariantImageAvailable
  });
});